import { images } from './images'

/** Operational bar under the hero — literal facts, readable by MANGO. */
export const trustFacts = [
  { value: '35+', label: 'Years serving our community', icon: 'award' as const },
  { value: 'Walk-ins', label: 'Welcome', icon: 'door' as const },
  { value: 'New patients', label: 'Yes, we are accepting', icon: 'users' as const },
  { value: 'English + Spanish', label: 'Bilingual care', icon: 'globe' as const },
]

/**
 * Real reviews from the "Lima Dental Miami Lakes" Google profile.
 * No patient photos are used, for privacy.
 */
export const reviews = [
  {
    name: 'Andrea M.',
    meta: 'Google review · 6 months ago',
    quote:
      'My experience at this dentist was amazing. Every single person working there was very kind and knowledgeable. After some not-so-great experiences at other dentists, this was a breath of fresh air. Dr. Lima is incredibly educated and genuinely cares about his patients.',
  },
  {
    name: 'Alberto S.',
    meta: 'Google review · 2 months ago',
    quote:
      'Had a great experience here. Dr. Lima was honest, reasonable, straightforward and very knowledgeable. There was a short wait time, the staff members were all very kind, and I never felt like I was getting scammed. They also have high tech equipment and a nice office.',
  },
  {
    name: 'The L. family',
    meta: 'Google review',
    quote:
      'We have been bringing our whole family here for years. They explain every option without pressuring us, and they always care for us in Spanish when we prefer it. This is the kind of office you come back to and recommend.',
  },
]

/** Clinic gallery — real Lima Dental Group photography. */
export const galleryItems = [
  {
    src: images.clinic.exterior,
    alt: 'Exterior entrance of the Lima Dental Group building with its signage in Miami Lakes, Florida',
    caption: 'Entrance · Miami Lakes',
    span: 'wide' as const,
    position: 'object-center',
  },
  {
    src: images.clinic.reception,
    alt: 'Lima Dental Group front desk with the Lima Dental logo on the reception wall',
    caption: 'Front Desk',
    span: 'tall' as const,
    position: 'object-center',
  },
  {
    src: images.clinic.waiting,
    alt: 'Digital panoramic and CBCT dental imaging unit at Lima Dental Group in Miami Lakes',
    caption: 'X-Ray Machine',
    span: 'normal' as const,
    position: 'object-center',
  },
  {
    src: images.clinic.treatment,
    alt: 'Lima Dental Group treatment room with a dental chair, overhead light and chairside monitor',
    caption: 'Treatment room',
    span: 'normal' as const,
    position: 'object-[center_60%]',
  },
  {
    src: images.clinic.technology,
    alt: 'Monitor showing a 3D dental scan of a patient at Lima Dental Group in Miami Lakes',
    caption: 'Real Time Xrays',
    span: 'normal' as const,
    position: 'object-center',
  },
]

/** Positioning: we are not a chain. */
export const principles = [
  {
    number: '01',
    title: '35+ years in the community',
    body: 'Decades caring for generations of families in Miami Lakes.',
  },
  {
    number: '02',
    title: 'Honest recommendations',
    body: 'We explain your options clearly and without pressure.',
  },
  {
    number: '03',
    title: 'Personal care',
    body: 'Your care is built around your needs, not around a sales quota.',
  },
]

/** Before / after cases — natural color, generic treatment labels. */
export const cases = [
  {
    id: 'case-1',
    title: 'Invisalign / alignment',
    before: images.cases.before1,
    after: images.cases.after1,
  },
  {
    id: 'case-2',
    title: 'Whitening',
    before: images.cases.before2,
    after: images.cases.after2,
  },
  {
    id: 'case-3',
    title: 'Porcelain veneers',
    before: images.cases.before3,
    after: images.cases.after3,
  },
  {
    id: 'case-4',
    title: 'Dental crown',
    before: images.cases.before4,
    after: images.cases.after4,
  },
  {
    id: 'case-5',
    title: 'Dental implant',
    before: images.cases.before5,
    after: images.cases.after5,
  },
  {
    id: 'case-6',
    title: 'Aesthetic restoration',
    before: images.cases.before6,
    after: images.cases.after6,
  },
]

/** Financing — confirmed information only. */
export const financing = {
  points: [
    'Financing options are available for patients who qualify.',
    'Payment options may be available depending on the treatment you need.',
    'Patients without dental insurance can ask about the options available.',
    'Our team explains every option before any treatment begins.',
  ],
  providers: [
    {
      name: 'CareCredit',
      description: 'Flexible healthcare financing options for qualifying patients.',
    },
    {
      name: 'Cherry Finance',
      description:
        'Flexible payment options designed to help qualifying patients manage treatment costs.',
    },
  ],
}

/** Lima VIP Membership — only confirmed benefits. */
export const membership = {
  name: 'Lima VIP Membership',
  price: '$50',
  period: '/ month',
  hook: 'No dental insurance? No problem.',
  body: 'With the Lima VIP Membership, $50 a month gives you access to cleanings and exams without relying on a traditional dental insurance plan.',
  includes: ['Cleanings', 'Exams'],
}

/**
 * Patient information — knowledge source for MANGO.
 * Every answer lives as text in the DOM.
 */
export const patientInfo = [
  {
    q: 'Do you accept walk-ins?',
    a: 'Yes. Walk-ins are welcome at Lima Dental Group. When possible, we recommend calling ahead so we can see you as quickly as possible.',
  },
  {
    q: 'Are you accepting new patients?',
    a: 'Yes. Lima Dental Group is accepting new patients.',
  },
  {
    q: 'Do you provide care in Spanish?',
    a: 'Yes. Lima Dental Group provides care in English and Spanish.',
  },
  {
    q: 'Where is Lima Dental Group located?',
    a: 'Lima Dental Group is located in Miami Lakes, Florida, at 15600 NW 67th Ave #106, Miami Lakes, FL 33014.',
  },
  {
    q: 'Do you treat dental emergencies?',
    a: 'Yes. If you have dental pain, a broken tooth, an infection or another urgent concern, call Lima Dental Group directly at (305) 556-2383 to request care.',
  },
  {
    q: 'Do you offer financing?',
    a: 'Yes. Lima Dental Group offers financing and payment options for patients who qualify, including CareCredit and Cherry Finance. Our team can explain the options available to you.',
  },
  {
    q: 'Can I come in if I do not have dental insurance?',
    a: 'Yes. Patients without dental insurance can join the Lima VIP Membership for $50 per month, which includes cleanings and exams. You can also contact our team to learn about the payment and financing options available.',
  },
  {
    q: 'How do I book an appointment?',
    a: 'You can book online at https://lima.dental, request an appointment using the form on this page, or call Lima Dental Group directly at (305) 556-2383.',
  },
  {
    q: 'Who is the dentist at Lima Dental Group?',
    a: 'Dr. Julio Lima, DDS is the dentist and owner of Lima Dental Group in Miami Lakes, Florida. The practice has been serving the Miami Lakes community since 1989.',
  },
]

export const footerLinks = [
  { label: 'Services', href: '#services' },
  { label: 'Invisalign', href: '#invisalign' },
  { label: 'Results', href: '#results' },
  { label: 'Financing', href: '#financing' },
  { label: 'Dr. Lima', href: '#dr-lima' },
  { label: 'Patient Info', href: '#patient-info' },
  { label: 'Emergency', href: '#emergency' },
  { label: 'Contact', href: '#contact' },
]

export const legalLinks = ['Privacy Policy', 'Terms', 'Accessibility']
